// Pure, UI-free helpers for the Docs Hub page: request-payload construction
// for the guarded content save / copy-forward restore, the 409-conflict and
// lock-conflict readers, the provenance line derived from revision rows, and
// the client-side +N/−N line delta. Kept React-free so each can be
// unit-tested directly. Selection now lives on path routes — see
// `explorer-paths.ts`.

import {
  createOntologyHeaders,
  getOntologyServiceBaseUrl,
  saveBlob,
  type Doc,
  type DocAuthorKind,
  type DocRevisionMeta,
  type DocStatus,
  type RestoreDocRevisionInput,
  type UpdateDocContentInput,
  type UpdateDocInput
} from '@axilon-apps/domains-ontology-ui-state'

import type { badgeVariants } from '../../components/ui/badge'
import { joinChildPath, parentPathOf } from '../../components/explorer'

type BadgeVariant = NonNullable<Parameters<typeof badgeVariants>[0]>['variant']

/** Formats the Docs Hub viewer can export (Design §9). */
export type DocsExportFormat = 'md' | 'docx' | 'pdf'

const EXPORT_EXTENSION: Record<DocsExportFormat, string> = {
  md: 'md',
  docx: 'docx',
  pdf: 'pdf'
}

/** Ensure a doc name reads as markdown in the tree, matching agent-written docs. */
export function withMarkdownSuffix(name: string): string {
  const trimmed = name.trim()
  return trimmed.toLowerCase().endsWith('.md') ? trimmed : `${trimmed}.md`
}

/**
 * Patch body for a doc rename. The rename dialog derives the leaf path from the
 * raw name, so re-derive it here to keep the `.md` suffix that create applies —
 * otherwise renaming strips it and the leaf stops matching the doc name.
 */
export function docRenameInput(name: string, path?: string): UpdateDocInput {
  const fileName = withMarkdownSuffix(name)
  if (path === undefined) return { name: fileName }
  return { name: fileName, path: joinChildPath(parentPathOf(path), fileName) }
}

/**
 * Filename for an Export-as download. Derives the stem from the full doc path
 * (slashes → hyphens) so `lines/airplane-engine-line/overview` exports as
 * `lines-airplane-engine-line-overview.docx`. Falls back to the doc name when
 * path is absent. Strips a trailing `.md` before attaching the export extension.
 */
export function exportDocFilename(docPath: string, format: DocsExportFormat): string {
  const trimmed = docPath.trim() || 'document'
  const slug = trimmed.replace(/\//g, '-')
  const stem = slug.toLowerCase().endsWith('.md') ? slug.slice(0, -3) : slug
  return `${stem}.${EXPORT_EXTENSION[format]}`
}

export interface DownloadDocExportArgs {
  format: DocsExportFormat
  doc: Pick<Doc, 'id' | 'name' | 'content' | 'path' | 'current_revision_id'>
  /** Export the revision shown by a chat artifact rather than the live document. */
  revisionId?: string
  /** Newest-first revision metas — used for client print-to-PDF provenance. */
  revisions?: readonly DocRevisionMeta[]
  /** Injected for tests; defaults to `globalThis.fetch`. */
  fetchImpl?: typeof fetch
  /** Injected for tests; defaults to shared `saveBlob`. */
  save?: (blob: Blob, fileName: string) => void
  /**
   * Injected for tests; defaults to {@link printDocAsPdf}. Used when the BFF
   * cannot produce a PDF (no LibreOffice / Playwright Chromium locally).
   */
  printPdf?: (args: PrintDocAsPdfArgs) => void
}

export type DownloadDocExportResult = { method: 'download' | 'print' }

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

/** Tiny Markdown→HTML for the browser print-to-PDF fallback (no authoring pkg). */
export function renderDocMarkdownToHtml(markdown: string): string {
  if (!markdown.trim()) return '<p><em>(empty document)</em></p>'
  const lines = markdown.replace(/\r\n/g, '\n').split('\n')
  const out: string[] = []
  let inCode = false
  let codeBuf: string[] = []
  let listKind: 'ul' | 'ol' | null = null
  let paraBuf: string[] = []

  const flushPara = (): void => {
    if (paraBuf.length === 0) return
    out.push(`<p>${renderDocInlineHtml(paraBuf.join(' '))}</p>`)
    paraBuf = []
  }
  const flushList = (): void => {
    if (listKind === 'ul') out.push('</ul>')
    else if (listKind === 'ol') out.push('</ol>')
    listKind = null
  }
  const openList = (kind: 'ul' | 'ol'): void => {
    if (listKind === kind) return
    flushList()
    out.push(kind === 'ul' ? '<ul>' : '<ol>')
    listKind = kind
  }

  for (const raw of lines) {
    if (inCode) {
      if (raw.trim().startsWith('```')) {
        out.push(`<pre><code>${escapeHtml(codeBuf.join('\n'))}</code></pre>`)
        codeBuf = []
        inCode = false
      } else {
        codeBuf.push(raw)
      }
      continue
    }
    if (raw.trim().startsWith('```')) {
      flushPara()
      flushList()
      inCode = true
      continue
    }
    const heading = /^(#{1,6})\s+(.+)$/.exec(raw)
    if (heading) {
      flushPara()
      flushList()
      const level = Math.min(heading[1].length, 6)
      out.push(`<h${level}>${renderDocInlineHtml(heading[2].trim())}</h${level}>`)
      continue
    }
    if (/^(?:-{3,}|\*{3,}|_{3,})\s*$/.test(raw)) {
      flushPara()
      flushList()
      out.push('<hr />')
      continue
    }
    const quote = /^>\s?(.*)$/.exec(raw)
    if (quote) {
      flushPara()
      flushList()
      out.push(`<blockquote>${renderDocInlineHtml(quote[1])}</blockquote>`)
      continue
    }
    const ul = /^\s*[-*+]\s+(.+)$/.exec(raw)
    if (ul) {
      flushPara()
      openList('ul')
      out.push(`<li>${renderDocInlineHtml(ul[1].trim())}</li>`)
      continue
    }
    const ol = /^\s*\d+\.\s+(.+)$/.exec(raw)
    if (ol) {
      flushPara()
      openList('ol')
      out.push(`<li>${renderDocInlineHtml(ol[1].trim())}</li>`)
      continue
    }
    if (raw.trim().length === 0) {
      flushPara()
      flushList()
      continue
    }
    paraBuf.push(raw)
  }
  if (inCode) out.push(`<pre><code>${escapeHtml(codeBuf.join('\n'))}</code></pre>`)
  flushPara()
  flushList()
  return out.join('\n')
}

function renderDocInlineHtml(s: string): string {
  let html = escapeHtml(s)
  html = html.replace(/`([^`]+)`/g, '<code>$1</code>')
  html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
  html = html.replace(/(^|\W)_([^_]+)_(?=\W|$)/g, '$1<em>$2</em>')
  html = html.replace(/\*([^*]+)\*/g, '<em>$1</em>')
  return html
}

const DOC_PRINT_STYLES = `
  @page { margin: 18mm 16mm; }
  body { font-family: Georgia, "Times New Roman", Times, serif; color: #111; line-height: 1.55; font-size: 11pt; }
  h1 { font-size: 20pt; margin: 0 0 10pt; font-family: system-ui, sans-serif; }
  h2 { font-size: 14pt; margin: 16pt 0 6pt; font-family: system-ui, sans-serif; }
  h3 { font-size: 12pt; margin: 12pt 0 4pt; font-family: system-ui, sans-serif; }
  h4, h5, h6 { font-size: 11pt; margin: 10pt 0 4pt; font-family: system-ui, sans-serif; }
  p { margin: 0 0 8pt; }
  ul, ol { margin: 0 0 8pt 18pt; padding: 0; }
  li { margin: 0 0 3pt; }
  blockquote { margin: 0 0 10pt; padding: 6pt 12pt; border-left: 3px solid #bbb; color: #333; background: #f7f7f8; }
  code { font-family: ui-monospace, Menlo, Consolas, monospace; font-size: 10pt; background: #f4f4f5; padding: 1px 4px; }
  pre { background: #f4f4f5; padding: 8pt; overflow-x: auto; font-size: 9.5pt; }
  pre code { background: transparent; padding: 0; }
  hr { border: none; border-top: 1px solid #ccc; margin: 14pt 0; }
  .provenance { margin-top: 18pt; font-size: 9.5pt; color: #444; }
`

export interface PrintDocAsPdfArgs {
  markdown: string
  title: string
  filename: string
  path?: string
  currentRevisionId?: string
  revisions?: readonly DocRevisionMeta[]
}

function formatRevisionAuthor(revision: DocRevisionMeta | undefined): string | null {
  if (!revision) return null
  const name = revision.author_name?.trim() ?? ''
  if (name === '') return null
  const kind = revision.author_kind?.trim() || 'user'
  return `${kind} · ${name}`
}

/** Build the printable HTML document used by the browser print-to-PDF fallback. */
export function buildDocPrintHtml(args: PrintDocAsPdfArgs): string {
  const revisions = args.revisions ?? []
  const latest = formatRevisionAuthor(revisions[0])
  const first = formatRevisionAuthor(
    revisions.length > 0 ? revisions[revisions.length - 1] : undefined
  )
  const path =
    typeof args.path === 'string' && args.path.trim() !== ''
      ? `/${args.path.replace(/^\/+/, '')}`
      : '(unknown)'
  const revision =
    typeof args.currentRevisionId === 'string' && args.currentRevisionId.trim() !== ''
      ? args.currentRevisionId.trim()
      : '(none)'
  const body = renderDocMarkdownToHtml(args.markdown)
  return `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(args.filename)}</title>
  <style>${DOC_PRINT_STYLES}</style>
</head>
<body>
${body}
<hr />
<div class="provenance">
  <strong>Export provenance</strong>
  <ul>
    <li>Path: ${escapeHtml(path)}</li>
    <li>Current revision: ${escapeHtml(revision)}</li>
    <li>First author: ${escapeHtml(first ?? '(none)')}</li>
    <li>Latest author: ${escapeHtml(latest ?? '(none)')}</li>
    <li>Exported at: ${escapeHtml(new Date().toISOString())}</li>
    <li>reference document — not a controlled copy</li>
  </ul>
</div>
</body>
</html>`
}

/**
 * Open the system print-to-PDF dialog for a Docs Hub document. Mirrors the
 * browser print dialog so Export as PDF still works
 * when the BFF has no LibreOffice / Playwright Chromium.
 */
export function printDocAsPdf(args: PrintDocAsPdfArgs): void {
  const html = buildDocPrintHtml(args)
  const iframe = document.createElement('iframe')
  iframe.style.position = 'fixed'
  iframe.style.right = '0'
  iframe.style.bottom = '0'
  iframe.style.width = '0'
  iframe.style.height = '0'
  iframe.style.border = '0'
  iframe.setAttribute('aria-hidden', 'true')
  document.body.appendChild(iframe)
  const iframeDoc = iframe.contentDocument
  if (!iframeDoc) {
    iframe.remove()
    throw new Error('PDF print fallback unavailable (no iframe document)')
  }
  iframeDoc.open()
  iframeDoc.write(html)
  iframeDoc.close()
  const cleanup = (): void => {
    window.setTimeout(() => iframe.remove(), 250)
  }
  const win = iframe.contentWindow
  if (!win) {
    cleanup()
    throw new Error('PDF print fallback unavailable (no iframe window)')
  }
  const onAfter = (): void => {
    win.removeEventListener('afterprint', onAfter)
    cleanup()
  }
  win.addEventListener('afterprint', onAfter)
  const trigger = (): void => {
    try {
      win.focus()
      win.print()
    } catch {
      cleanup()
      throw new Error('PDF print fallback failed')
    }
  }
  if (iframeDoc.readyState === 'complete') trigger()
  else iframe.addEventListener('load', trigger, { once: true })
}

/**
 * Run a Docs Hub export.
 *
 * - **Markdown** — client-side blob of the already-loaded content (no BFF).
 * - **DOCX** — `GET /api/ontology/docs/:id/export?format=docx` with
 *   `createOntologyHeaders()`, then save the bytes.
 * - **PDF** — same BFF route when LibreOffice/Chromium is available; otherwise
 *   falls back to browser print-to-PDF (agent-report pattern). The UI never
 *   imports `@atbash-labs/markdown-authoring`.
 */
export async function downloadDocExport(
  args: DownloadDocExportArgs
): Promise<DownloadDocExportResult> {
  const save = args.save ?? saveBlob
  const filename = exportDocFilename(args.doc.path, args.format)

  if (args.format === 'md') {
    save(new Blob([args.doc.content ?? ''], { type: 'text/markdown;charset=utf-8' }), filename)
    return { method: 'download' }
  }

  const fetchImpl = args.fetchImpl ?? fetch
  const query = new URLSearchParams({ format: args.format })
  if (args.revisionId) query.set('revision_id', args.revisionId)
  const url = `${getOntologyServiceBaseUrl()}/docs/${encodeURIComponent(args.doc.id)}/export?${query}`

  const fallbackPdfPrint = (): DownloadDocExportResult => {
    const print = args.printPdf ?? printDocAsPdf
    print({
      markdown: args.doc.content ?? '',
      title: args.doc.name,
      filename,
      path: args.doc.path,
      currentRevisionId: args.doc.current_revision_id,
      revisions: args.revisions
    })
    return { method: 'print' }
  }

  let res: Response
  try {
    res = await fetchImpl(url, { headers: createOntologyHeaders() })
  } catch (err) {
    if (args.format === 'pdf') {
      throw new Error(`PDF export failed: ${err instanceof Error ? err.message : 'network error'}`)
    }
    throw err
  }

  if (!res.ok) {
    const text = await res.text().catch(() => '')
    if (args.format === 'pdf' && res.status === 503) {
      let reason: string | undefined
      try {
        const body = JSON.parse(text) as { reason?: string; error?: string }
        reason = body.reason ?? body.error
      } catch {
        /* not json */
      }
      if (reason === 'DOCS_EXPORT_PDF_UNAVAILABLE') return fallbackPdfPrint()
    }
    throw new Error(
      `${args.format.toUpperCase()} export failed (${res.status})${text ? `: ${text}` : ''}`
    )
  }
  save(await res.blob(), filename)
  return { method: 'download' }
}

/**
 * Body for `PUT /docs/{id}/content`. `expectedRevisionId` is omitted (not
 * nulled) for a doc that has never been saved — the proxy's guard matches a
 * NULL `current_revision_id` only when the field is absent.
 */
export function buildContentSavePayload(args: {
  content: string
  lockToken: string
  expectedRevisionId?: string
  author: string
  authorKind?: DocAuthorKind
  summary?: string
}): UpdateDocContentInput {
  const payload: UpdateDocContentInput = {
    content: args.content,
    lock_token: args.lockToken,
    author_kind: args.authorKind ?? 'user',
    author: args.author
  }
  if (args.expectedRevisionId !== undefined && args.expectedRevisionId !== '') {
    payload.expected_revision_id = args.expectedRevisionId
  }
  const summary = args.summary?.trim() ?? ''
  if (summary !== '') payload.summary = summary
  return payload
}

/**
 * Body for `POST /docs/{id}/revisions/{revId}:restore` — the same guards as a
 * save, because restore is copy-forward: it appends a revision holding the old
 * content instead of rewinding history.
 */
export function buildRestorePayload(args: {
  lockToken: string
  expectedRevisionId?: string
  author: string
  authorKind?: DocAuthorKind
  summary?: string
}): RestoreDocRevisionInput {
  const payload: RestoreDocRevisionInput = {
    lock_token: args.lockToken,
    author_kind: args.authorKind ?? 'user',
    author: args.author
  }
  if (args.expectedRevisionId !== undefined && args.expectedRevisionId !== '') {
    payload.expected_revision_id = args.expectedRevisionId
  }
  const summary = args.summary?.trim() ?? ''
  if (summary !== '') payload.summary = summary
  return payload
}

interface ProxyErrorEnvelope {
  status?: unknown
  data?: {
    message?: unknown
    reason?: unknown
    details?: Record<string, unknown>
  }
}

function envelope(err: unknown): ProxyErrorEnvelope | null {
  return typeof err === 'object' && err !== null ? (err as ProxyErrorEnvelope) : null
}

function detailString(details: Record<string, unknown> | undefined, key: string): string {
  const value = details?.[key]
  return typeof value === 'string' ? value : ''
}

/** The 409 save-guard payload: the server's current revision + a 4 KiB excerpt. */
export interface RevisionConflict {
  currentRevisionId: string
  contentExcerpt: string
}

/**
 * Read a `DOC_REVISION_CONFLICT` (409) out of an RTK Query error. Returns null
 * for every other failure so callers can fall through to a plain toast.
 */
export function readRevisionConflict(err: unknown): RevisionConflict | null {
  const shape = envelope(err)
  if (shape?.status !== 409 || shape.data?.reason !== 'DOC_REVISION_CONFLICT') return null
  return {
    currentRevisionId: detailString(shape.data.details, 'current_revision_id'),
    contentExcerpt: detailString(shape.data.details, 'content_excerpt')
  }
}

/** The 409 acquire payload: who holds the lease and until when. */
export interface LockConflict {
  holderKind: string
  holderName: string
  expiresAt: string
}

/** Read a `DOC_LOCK_HELD` (409) out of an RTK Query error; null otherwise. */
export function readLockConflict(err: unknown): LockConflict | null {
  const shape = envelope(err)
  if (shape?.status !== 409 || shape.data?.reason !== 'DOC_LOCK_HELD') return null
  return {
    holderKind: detailString(shape.data.details, 'lock_holder_kind'),
    holderName: detailString(shape.data.details, 'lock_holder_name'),
    expiresAt: detailString(shape.data.details, 'lock_expires_at')
  }
}

/** Human-readable message for any proxy error, falling back to `fallback`. */
export function readErrorMessage(err: unknown, fallback: string): string {
  const shape = envelope(err)
  if (typeof shape?.data?.message === 'string' && shape.data.message !== '') {
    return shape.data.message
  }
  return err instanceof Error ? err.message : fallback
}

/**
 * True once a lease's expiry has passed, which is exactly when the proxy lets
 * the next caller take it over. An unparseable/absent expiry counts as expired
 * (the proxy re-checks anyway, so a wrong guess only shows the wrong button).
 */
export function isLeaseExpired(expiresAt: string, now: Date = new Date()): boolean {
  if (expiresAt === '') return true
  const expiry = Date.parse(expiresAt)
  return Number.isNaN(expiry) ? true : expiry <= now.getTime()
}

/**
 * The doc's lease as the Edit button needs to read it:
 * - `free` — nobody holds it, edit normally;
 * - `held` — someone else is editing; the button names them and stays disabled;
 * - `expired` — a leaked lease (crashed tab, killed agent run) that the proxy
 *   will hand over on the next acquire, so the button offers a takeover.
 */
export interface LeaseState {
  state: 'free' | 'held' | 'expired'
  holderKind: DocAuthorKind
  holderName: string
  expiresAt: string
}

export function describeLease(
  doc: Pick<Doc, 'lock_holder_kind' | 'lock_holder_name' | 'lock_expires_at'>,
  now: Date = new Date()
): LeaseState {
  const expiresAt = doc.lock_expires_at ?? ''
  const holderName = doc.lock_holder_name ?? ''
  if (expiresAt === '' || holderName === '') {
    return { state: 'free', holderKind: 'user', holderName: '', expiresAt: '' }
  }
  return {
    state: isLeaseExpired(expiresAt, now) ? 'expired' : 'held',
    holderKind: doc.lock_holder_kind ?? 'user',
    holderName,
    expiresAt
  }
}

/** "12:01" — the wall-clock expiry shown beside a foreign lease. */
export function clockTime(iso: string): string {
  const parsed = new Date(iso)
  if (Number.isNaN(parsed.getTime())) return ''
  return parsed.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
}

const MINUTE_MS = 60_000
const HOUR_MS = 60 * MINUTE_MS
const DAY_MS = 24 * HOUR_MS

/** "just now" / "3 days ago" — coarse, matching the revision-list density. */
export function relativeTime(iso: string, now: Date = new Date()): string {
  const then = Date.parse(iso)
  if (Number.isNaN(then)) return ''
  const delta = now.getTime() - then
  if (delta < MINUTE_MS) return 'just now'
  if (delta < HOUR_MS) {
    const minutes = Math.round(delta / MINUTE_MS)
    return `${minutes}m ago`
  }
  if (delta < DAY_MS) {
    const hours = Math.round(delta / HOUR_MS)
    return `${hours}h ago`
  }
  const days = Math.round(delta / DAY_MS)
  return days === 1 ? '1 day ago' : `${days} days ago`
}

/** "Jul 14" — the short date used for the original draft. */
export function shortDate(iso: string): string {
  const parsed = new Date(iso)
  if (Number.isNaN(parsed.getTime())) return ''
  return parsed.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

/**
 * The viewer's provenance line, derived entirely from revision rows (newest
 * first, as the proxy returns them): who drafted the doc, who last edited it,
 * and how many revisions exist. When the history is paginated the oldest row on
 * hand is not the original draft, so the "Drafted by" clause is dropped and the
 * count is marked as a floor rather than claiming a total we do not have.
 */
export function buildProvenanceLine(
  revisions: readonly DocRevisionMeta[],
  options: { hasMore?: boolean; now?: Date } = {}
): string {
  if (revisions.length === 0) return 'No revisions yet'
  const now = options.now ?? new Date()
  const hasMore = options.hasMore === true
  const parts: string[] = []

  if (!hasMore) {
    const first = revisions[revisions.length - 1]
    parts.push(
      `Drafted by ${first.author_kind} · ${first.author_name}`,
      shortDate(first.created_at)
    )
  }

  const latest = revisions[0]
  if (hasMore || revisions.length > 1) {
    parts.push(`edited by ${latest.author_name}`, relativeTime(latest.created_at, now))
  }

  const count = hasMore
    ? `${revisions.length}+ revisions`
    : `${revisions.length} revision${revisions.length === 1 ? '' : 's'}`
  parts.push(count)

  return parts.filter((part) => part !== '').join(' · ')
}

function lineCounts(content: string): Map<string, number> {
  const counts = new Map<string, number>()
  if (content === '') return counts
  for (const line of content.split('\n')) {
    counts.set(line, (counts.get(line) ?? 0) + 1)
  }
  return counts
}

/**
 * Client-side +N/−N for a revision: lines present in `next` but not in
 * `previous`, and vice versa, compared as multisets. Deliberately not an LCS
 * diff — the counts only have to summarize a revision's size at a glance, and a
 * quadratic diff over a 1 MiB doc would stall the history panel. Moved lines
 * therefore count as neither added nor removed.
 */
export function revisionLineDelta(
  previous: string,
  next: string
): { added: number; removed: number } {
  const before = lineCounts(previous)
  const after = lineCounts(next)
  let added = 0
  let removed = 0
  for (const [line, count] of after) {
    added += Math.max(0, count - (before.get(line) ?? 0))
  }
  for (const [line, count] of before) {
    removed += Math.max(0, count - (after.get(line) ?? 0))
  }
  return { added, removed }
}

/**
 * The chat turn behind "Update with agent": `query_agent` owns reads and writes,
 * so this only has to name the doc. The open doc also rides the page's app
 * context, but naming the path here keeps the instruction self-contained
 * if the user edits or resends it later.
 */
export function buildDocRefreshInstruction(doc: Pick<Doc, 'name' | 'path'>): string {
  return [
    `Update the doc at /${doc.path} ("${doc.name}").`,
    'Re-read it, check every claim against the ontology and live data, and save a revision',
    'with a summary of what drifted. Mark anything you cannot verify with a',
    '"> **Gap for a human:**" callout, and never invent tag names.',
    'If nothing has drifted, say the doc is current instead of saving a revision.'
  ].join(' ')
}

/** Human-readable byte size: 1536 -> "1.5 KB". */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  const exp = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1)
  const value = bytes / 1024 ** exp
  const rounded = exp === 0 ? value : Math.round(value * 10) / 10
  return `${rounded} ${units[exp]}`
}

/** Display label + badge variant for a review status. */
export function statusMeta(status: DocStatus): { label: string; variant: BadgeVariant } {
  switch (status) {
    case 'in_review':
      return { label: 'Ready', variant: 'secondary' }
    case 'approved':
      return { label: 'Approved', variant: 'default' }
    case 'published':
      return { label: 'Published', variant: 'default' }
    case 'draft':
    default:
      return { label: 'Draft', variant: 'outline' }
  }
}

/**
 * The review-status workflow, mirroring the proxy's enforced transition
 * map (`docs.CanTransitionStatus`). The UI only offers buttons for
 * reachable transitions; the proxy is still the authority and rejects
 * anything else with 409.
 */
const STATUS_TRANSITIONS: Record<DocStatus, DocStatus[]> = {
  draft: ['in_review'],
  in_review: ['approved', 'draft'],
  approved: ['published', 'in_review'],
  published: []
}

/** The statuses a document may move to from its current status. */
export function nextStatuses(status: DocStatus): DocStatus[] {
  return STATUS_TRANSITIONS[status] ?? []
}

export interface StatusAction {
  to: DocStatus
  label: string
}

/**
 * The labelled status actions available from a document's current status —
 * forward edges get a verb ("Mark ready", "Approve", "Publish"),
 * backward edges read as "Send back to …".
 */
export function statusActions(status: DocStatus): StatusAction[] {
  return nextStatuses(status).map((to) => ({ to, label: transitionLabel(status, to) }))
}

function transitionLabel(from: DocStatus, to: DocStatus): string {
  const forward = STATUS_ORDER[to] > STATUS_ORDER[from]
  if (!forward) {
    if (to === 'draft') return 'Mark draft'
    return `Send back to ${statusMeta(to).label.toLowerCase()}`
  }
  switch (to) {
    case 'in_review':
      return 'Mark ready'
    case 'approved':
      return 'Approve'
    case 'published':
      return 'Publish'
    default:
      return `Move to ${statusMeta(to).label.toLowerCase()}`
  }
}

const STATUS_ORDER: Record<DocStatus, number> = {
  draft: 0,
  in_review: 1,
  approved: 2,
  published: 3
}

/**
 * How a document of `format` should be previewed in-browser:
 *  - `pdf`  → the browser renders the bytes natively (iframe + object URL);
 *  - `html` → the BFF converts it to an HTML fragment (docx/xlsx/csv/…);
 *  - `none` → no preview path; the UI offers download instead.
 */
const HTML_PREVIEW_FORMATS = new Set(['docx', 'xlsx', 'xlsm', 'xltx', 'xltm', 'csv'])

export function previewKind(format: string): 'pdf' | 'html' | 'none' {
  const normalized = format.trim().toLowerCase()
  if (normalized === 'pdf') return 'pdf'
  if (HTML_PREVIEW_FORMATS.has(normalized)) return 'html'
  return 'none'
}

/** Build a download filename from a row's display name + format. */
export function downloadName(name: string, format: string): string {
  if (!format) return name
  return name.toLowerCase().endsWith(`.${format.toLowerCase()}`) ? name : `${name}.${format}`
}

/** A review comment plus its (recursively nested) replies. */
export interface CommentLike {
  id: string
  parent_comment_id?: string | null
}

export interface CommentNode<T extends CommentLike = CommentLike> {
  comment: T
  replies: CommentNode<T>[]
}

/**
 * Build the threaded comment tree from the proxy's flat, oldest-first
 * comment list. Each node's replies preserve the source order. A comment
 * whose parent is missing (e.g. a deleted-then-orphaned edge, which the
 * proxy's cascade prevents but we guard anyway) is treated as top-level so
 * it is never silently dropped.
 */
export function buildCommentTree<T extends CommentLike>(comments: readonly T[]): CommentNode<T>[] {
  const nodes = new Map<string, CommentNode<T>>()
  for (const comment of comments) {
    nodes.set(comment.id, { comment, replies: [] })
  }
  const roots: CommentNode<T>[] = []
  for (const comment of comments) {
    const node = nodes.get(comment.id)
    if (!node) continue
    const parentId = comment.parent_comment_id
    const parent = parentId != null ? nodes.get(parentId) : undefined
    if (parent) parent.replies.push(node)
    else roots.push(node)
  }
  return roots
}
